const CRO_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

if (!customElements.get('cro-hero-footnote')) {
  customElements.define(
    'cro-hero-footnote',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.tooltip = this.querySelector('[role="tooltip"]');
        this.pinned = false;
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.button.addEventListener('click', () => {
          if (this.pinned) this.dismiss();
          else {
            this.pinned = true;
            this.open();
          }
        }, options);
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.open();
        }, options);
        this.addEventListener('pointerleave', () => {
          if (!this.pinned && !this.contains(document.activeElement)) this.close();
        }, options);
        this.button.addEventListener('focus', () => this.open(), options);
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.close();
        }, options);
        document.addEventListener('click', (event) => {
          if (!this.tooltip.hidden && !this.contains(event.target)) this.close();
        }, options);
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.tooltip.hidden) {
            event.preventDefault();
            if (this.pinned) this.dismiss();
            else this.close();
          }
        }, options);
        window.addEventListener('resize', () => {
          if (!this.tooltip.hidden) this.position();
        }, options);
      }

      open() {
        this.tooltip.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        this.position();
      }

      close() {
        this.pinned = false;
        this.tooltip.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
      }

      dismiss() {
        this.button.focus({ preventScroll: true });
        this.close();
      }

      position() {
        this.tooltip.style.removeProperty('inset-inline-start');
        this.tooltip.style.removeProperty('inset-block-start');
        if (window.matchMedia('(min-width: 768px)').matches) return;
        const anchor = this.getBoundingClientRect();
        const label = this.parentElement.getBoundingClientRect();
        const width = this.tooltip.getBoundingClientRect().width;
        const gutter = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const left = Math.max(gutter, Math.min(anchor.left + anchor.width / 2 - width / 2, document.documentElement.clientWidth - width - gutter));
        this.tooltip.style.insetInlineStart = `${left - anchor.left}px`;
        this.tooltip.style.insetBlockStart = `${label.bottom - anchor.top}px`;
      }

      disconnectedCallback() {
        this.events.abort();
      }
    }
  );
}

if (!customElements.get('cro-hero-slider')) {
  customElements.define(
    'cro-hero-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.cro-hero2__track');
        this.slides = Array.from(this.querySelectorAll('.cro-hero2__slide'));
        this.dots = Array.from(this.querySelectorAll('.cro-hero2__dot'));
        this.index = 0;
        if (CRO_REDUCED_MOTION.matches) this.track.style.transition = 'none';
        this.querySelector('.cro-hero2__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('.cro-hero2__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
        this.addEventListener('touchstart', (e) => {
          this.swipeX = e.touches[0].clientX;
          this.swipeY = e.touches[0].clientY;
        }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (this.swipeX == null) return;
          const dx = e.changedTouches[0].clientX - this.swipeX;
          const dy = e.changedTouches[0].clientY - this.swipeY;
          this.swipeX = null;
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1));
        }, { passive: true });
        this.update();
      }

      go(index) {
        this.index = (index + this.slides.length) % this.slides.length;
        this.update();
      }

      update() {
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
        this.slides.forEach((slide, i) => {
          slide.toggleAttribute('inert', i !== this.index);
          slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
        });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      }
    }
  );
}

if (!customElements.get('cro-text-rotator')) {
  customElements.define(
    'cro-text-rotator',
    class extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.cro-hero2__rotate-item'));
        this.current = 0;
        this.items.forEach((item, i) => item.classList.toggle('is-cro-active', i === 0));
        if (this.items.length < 2 || CRO_REDUCED_MOTION.matches) return;
        this.animations = new Map();
        const style = getComputedStyle(this.items[1]);
        this.timing = {
          duration: parseFloat(style.transitionDuration) * 1000,
          easing: style.transitionTimingFunction,
        };
        this.zone = this.closest('.cro-hero2__slide') || this;
        this.pause = () => this.stop();
        this.resume = () => { if (!this.zone.matches(':hover, :focus-within')) this.start(); };
        ['mouseenter', 'focusin'].forEach((e) => this.zone.addEventListener(e, this.pause));
        ['mouseleave', 'focusout'].forEach((e) => this.zone.addEventListener(e, () => setTimeout(this.resume)));
        this.start();
      }

      start() {
        if (!this.timer) this.timer = setInterval(() => this.advance(), 2500);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }

      disconnectedCallback() {
        this.stop();
        this.animations?.forEach((animation) => animation.cancel());
      }

      advance() {
        const prev = this.items[this.current];
        this.current = (this.current + 1) % this.items.length;
        const next = this.items[this.current];
        this.animations.get(prev)?.cancel();
        this.animations.get(next)?.cancel();
        prev.classList.remove('is-cro-active');
        next.classList.add('is-cro-active');
        this.animations.set(prev, prev.animate([
          { translate: '0 0', opacity: 1 },
          { translate: '0 -105%', opacity: 0 },
        ], this.timing));
      }
    }
  );
}
